import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const BASE_URL = '/api/v1/verbandbuch';

const useVerbandbuch = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  // Branch options for the entry form.
  const getBranches = useCallback(async () => {
    return sendRequest({
      url: `${BASE_URL}/branches`,
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
  }, [authCtx, sendRequest]);

  // Creates a Verbandbuch (first-aid log) entry.
  const createEntry = useCallback(
    async body => {
      return sendRequest({
        url: BASE_URL,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body,
      });
    },
    [authCtx, sendRequest]
  );

  // Entries plus aggregates for the evaluation view (scoped by the backend).
  const getStatistics = useCallback(
    async ({ from, to, branchId } = {}) => {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (branchId) params.branchId = branchId;
      return sendRequest({
        url: `${BASE_URL}/statistics`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
    },
    [authCtx, sendRequest]
  );

  // Downloads the evaluation as a CSV or Excel blob.
  const downloadExport = useCallback(
    async (format, { from, to, branchId } = {}) => {
      const params = { format };
      if (from) params.from = from;
      if (to) params.to = to;
      if (branchId) params.branchId = branchId;
      return sendRequest({
        url: `${BASE_URL}/statistics/export`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
        returnBlob: true,
      });
    },
    [authCtx, sendRequest]
  );

  // Removes an entry (corrections; needs verbandbuch:delete).
  const deleteEntry = useCallback(
    async id => {
      return sendRequest({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
        headers: authCtx.getAuthHeaders(),
      });
    },
    [authCtx, sendRequest]
  );

  return { isLoading, error, getBranches, createEntry, getStatistics, downloadExport, deleteEntry };
};

export default useVerbandbuch;
